import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Trophy,
  Clock,
  CheckCircle,
  XCircle,
  ArrowRight,
  AlertCircle,
  Gamepad2,
  Crown,
  Timer,
  Star,
  ArrowLeft,
  User,
  Lock,
  Gift,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// Character reveal sound - dramatic "unlock" effect
const playCharacterRevealSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a dramatic "unlock/reveal" sound sequence
    const playTone = (freq, startTime, duration, volume = 0.3) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + startTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + startTime + duration);
      
      oscillator.start(audioContext.currentTime + startTime);
      oscillator.stop(audioContext.currentTime + startTime + duration);
    };
    
    // Dramatic ascending sequence (like a treasure reveal)
    // First: suspense build-up (low notes)
    playTone(196, 0, 0.15, 0.2);      // G3
    playTone(220, 0.12, 0.15, 0.25);  // A3
    playTone(262, 0.24, 0.15, 0.3);   // C4
    
    // Then: triumphant reveal (higher notes)
    playTone(330, 0.4, 0.2, 0.35);    // E4
    playTone(392, 0.55, 0.2, 0.4);    // G4
    playTone(523, 0.7, 0.4, 0.45);    // C5 (victory note, held longer)
    
    // Final sparkle (very high, quick)
    playTone(784, 0.95, 0.15, 0.2);   // G5
    playTone(1047, 1.05, 0.3, 0.15);  // C6 (sparkle)
    
  } catch (e) {
    // Silent fail if audio not supported
    console.log('Audio not supported');
  }
};

// Failure sound - sad descending sequence
const playFailureSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playTone = (freq, startTime, duration, volume = 0.25) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + startTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + startTime + duration);
      
      oscillator.start(audioContext.currentTime + startTime);
      oscillator.stop(audioContext.currentTime + startTime + duration);
    };
    
    // Sad descending sequence (minor key feel)
    playTone(392, 0, 0.3, 0.3);       // G4
    playTone(349, 0.25, 0.3, 0.25);   // F4
    playTone(311, 0.5, 0.3, 0.2);     // Eb4 (minor third)
    playTone(262, 0.75, 0.5, 0.15);   // C4 (sad resolve, held longer)
    
  } catch (e) {
    // Silent fail
  }
};

// Celebration function with confetti burst
const triggerCelebration = () => {
  // Create multiple bursts for a dramatic effect
  const duration = 3000;
  const animationEnd = Date.now() + duration;
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

  function randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  // Main confetti burst
  const interval = setInterval(function() {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(interval);
    }

    const particleCount = 50 * (timeLeft / duration);
    
    // Red and gold confetti - Casa de Papel theme
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      colors: ['#dc2626', '#fbbf24', '#ffffff', '#991b1b', '#f59e0b']
    });
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      colors: ['#dc2626', '#fbbf24', '#ffffff', '#991b1b', '#f59e0b']
    });
  }, 250);

  // Initial big burst from center
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#dc2626', '#fbbf24', '#ffffff'],
    zIndex: 9999
  });

  // Play celebration sound
  try {
    const celebrationSound = new Audio('/sounds/celebration.mp3');
    celebrationSound.volume = 0.5;
    celebrationSound.play().catch(() => {
      // Fallback: use Web Audio API for a simple success tone
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
      oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    });
  } catch (e) {
    // Silent fail if audio not supported
  }
};

// Matrix Rain Effect Component - Casa de Papel Theme
const MatrixRain = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789€$£¥₿#@&%?!';
    const charArray = chars.split('');
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    
    const drops = [];
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100;
    }
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const char = charArray[Math.floor(Math.random() * charArray.length)];
        const brightness = Math.random();
        if (brightness > 0.95) {
          ctx.fillStyle = '#ffffff';
        } else if (brightness > 0.7) {
          ctx.fillStyle = '#ff3333';
        } else {
          ctx.fillStyle = '#8b0000';
        }
        ctx.fillText(char, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 50);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0 opacity-15"
      style={{ mixBlendMode: 'screen' }}
    />
  );
};

const GamePlayPage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [gameState, setGameState] = useState('loading'); // loading, intro, playing, finished
  const [results, setResults] = useState(null);
  const [player, setPlayer] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(0);
  const [celebrationTriggered, setCelebrationTriggered] = useState(false);

  // Trigger celebration when character is earned and redirect after delay
  useEffect(() => {
    if (gameState === 'finished' && results && game?.month && !celebrationTriggered) {
      const passedQuiz = results.correct_count >= (results.total_questions / 2);
      setCelebrationTriggered(true);
      
      if (passedQuiz) {
        // Play character reveal sound first (dramatic)
        setTimeout(() => {
          playCharacterRevealSound();
        }, 300);
        
        // Then confetti celebration
        setTimeout(() => {
          triggerCelebration();
        }, 800);
        
        // Redirect to player dashboard after 12 seconds (3x longer to enjoy the animation)
        setTimeout(() => {
          navigate('/ma-progression-coffre');
        }, 12000);
      } else {
        // Quiz failed - play sad sound
        setTimeout(() => {
          playFailureSound();
        }, 300);
        
        // No automatic redirect - let user choose to retry or leave
      }
    }
  }, [gameState, results, game, celebrationTriggered, navigate]);

  useEffect(() => {
    checkAuth();
    fetchGame();
  }, [gameId]);

  useEffect(() => {
    let timer;
    if (gameState === 'playing' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameState, timeLeft, currentIndex]);

  const checkAuth = async () => {
    const token = localStorage.getItem('game_token');
    if (!token) {
      toast.error('Connectez-vous pour jouer');
      navigate(`/jeu/${gameId}/connexion`);
      return;
    }
    try {
      const res = await api.get(`/game/players/me?token=${token}`);
      setPlayer(res.data);
    } catch (e) {
      localStorage.removeItem('game_token');
      navigate(`/jeu/${gameId}/connexion`);
    }
  };

  const fetchGame = async () => {
    try {
      const res = await api.get(`/game/contests/${gameId}`);
      setGame(res.data);
      setTimeLeft(res.data.time_limit_seconds || 60);
      setGameState('intro');
    } catch (e) {
      toast.error('Jeu introuvable');
      navigate('/');
    }
  };

  const startGame = async () => {
    const token = localStorage.getItem('game_token');
    try {
      const res = await api.get(`/game/contests/${gameId}/questions?token=${token}`);
      if (res.data.length === 0) {
        toast.error('Ce jeu n\'a pas encore de questions');
        return;
      }
      setQuestions(res.data);
      setGameState('playing');
      setCurrentIndex(0);
      setTimeLeft(game.time_limit_seconds || 60);
      setQuestionStartTime(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du chargement');
    }
  };

  const handleTimeUp = () => {
    // Auto-submit wrong answer when time runs out
    handleAnswer(-1);
  };

  const handleAnswer = (answerIndex) => {
    const timeTaken = Date.now() - questionStartTime;
    const currentQuestion = questions[currentIndex];

    setAnswers(prev => [...prev, {
      question_id: currentQuestion.id,
      answer_index: answerIndex,
      time_taken_ms: timeTaken
    }]);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setTimeLeft(game.time_limit_seconds || 60);
      setQuestionStartTime(Date.now());
    } else {
      submitGame([...answers, {
        question_id: currentQuestion.id,
        answer_index: answerIndex,
        time_taken_ms: timeTaken
      }]);
    }
  };

  const submitGame = async (finalAnswers) => {
    setGameState('submitting');
    const token = localStorage.getItem('game_token');
    try {
      const res = await api.post(`/game/contests/${gameId}/submit?token=${token}`, finalAnswers);
      setResults(res.data);
      setGameState('finished');
    } catch (e) {
      toast.error('Erreur lors de la soumission');
      setGameState('finished');
    }
  };

  const currentQuestion = questions[currentIndex];
  const progressPercent = questions.length > 0 ? ((currentIndex) / questions.length) * 100 : 0;
  const timePercent = game ? (timeLeft / (game.time_limit_seconds || 60)) * 100 : 100;

  // Loading state
  if (gameState === 'loading') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        <MatrixRain />
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 relative z-10"></div>
      </div>
    );
  }

  // Intro state
  if (gameState === 'intro' && game) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        <MatrixRain />
        <div className="fixed inset-0 bg-gradient-to-b from-red-900/20 via-black/30 to-red-900/20 pointer-events-none z-0" />
        
        {/* Header */}
        <header className="border-b border-red-500/30 bg-black/95 backdrop-blur-md fixed top-0 left-0 right-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/le-coffre-msc" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-950/80 hover:bg-red-900 transition-colors border border-red-500/30">
              <ArrowLeft className="w-4 h-4 text-red-400" />
              <span className="text-sm text-white">Retour</span>
            </Link>
            {player && (
              <Link 
                to="/mon-espace-joueur" 
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition-colors shadow-lg shadow-red-500/30"
              >
                <User className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-semibold">Mon Espace Joueur</span>
              </Link>
            )}
          </div>
        </header>

        <div className="flex items-center justify-center p-4 min-h-screen pt-20 relative z-10">
          <Card className="max-w-lg w-full bg-black border-2 border-red-500/50 shadow-xl shadow-red-500/20">
          <CardHeader className="text-center">
            {game.is_main_game && (
              <Badge className="bg-gradient-to-r from-red-600 to-red-700 mx-auto mb-4">
                <Crown className="w-3 h-3 mr-1" />
                Le Coffre MSC
              </Badge>
            )}
            {game.month && (
              <Badge className="bg-red-600 mx-auto mb-4">
                Quiz Mensuel - Mois {game.month}
              </Badge>
            )}
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/40">
              <Gamepad2 className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">{game.title}</CardTitle>
            <p className="text-white/70">{game.description}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-red-950/80 rounded-lg p-4 border border-red-500/30">
                <Timer className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-sm text-white/60">Temps par question</p>
                <p className="text-xl font-bold text-white">{game.time_limit_seconds}s</p>
              </div>
              <div className="bg-red-950/80 rounded-lg p-4 border border-red-500/30">
                <Star className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                <p className="text-sm text-white/60">Points par réponse</p>
                <p className="text-xl font-bold text-white">{game.points_per_correct} pts</p>
              </div>
            </div>

            {/* Info sur le caractère à gagner */}
            {game.month && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <Lock className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-sm text-white/60">En validant ce quiz (≥50% de bonnes réponses), vous obtenez</p>
                <p className="text-lg font-semibold text-green-400">1 caractère de la combinaison du coffre !</p>
              </div>
            )}

            {/* Info sur le système de classement */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <Trophy className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-sm text-white/80 text-center">
                <strong className="text-yellow-400">🏆 Classement annuel</strong><br/>
                Vos points s'accumulent sur tous les quiz de l'année !<br/>
                <span className="text-white/60">Les meilleurs joueurs au 31 décembre remportent des lots selon leur classement.</span>
              </p>
            </div>

            {/* Info sur la possibilité de rejouer */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <AlertCircle className="w-5 h-5 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-white/80">
                <strong className="text-red-400">⚠️ Important</strong><br/>
                Vous pouvez <strong>retenter ce quiz</strong> pour améliorer votre score tant que vous n'avez pas validé (≥50%).<br/>
                <span className="text-white/60">Une fois validé, le quiz est verrouillé et vous obtenez le caractère du mois.</span>
              </p>
            </div>

            {game.time_bonus_enabled && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
                <p className="text-red-300 text-sm">
                  ⚡ Bonus de rapidité activé ! Répondez vite pour gagner plus de points.
                </p>
              </div>
            )}

            {player && (
              <div className="text-center text-white/60 text-sm">
                Connecté en tant que <span className="text-red-400">{player.first_name}</span>
              </div>
            )}

            <Button
              onClick={startGame}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-lg py-6 shadow-lg shadow-red-500/30"
            >
              <Gamepad2 className="w-5 h-5 mr-2" />
              Commencer le quiz
            </Button>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  // Playing state
  if (gameState === 'playing' && currentQuestion) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        <MatrixRain />
        <div className="fixed inset-0 bg-gradient-to-b from-red-900/20 via-black/30 to-red-900/20 pointer-events-none z-0" />
        
        {/* Header with back button and profile */}
        <header className="border-b border-red-500/30 bg-black/95 backdrop-blur-md fixed top-0 left-0 right-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
            <button 
              onClick={() => {
                if (confirm('Voulez-vous vraiment quitter le quiz ? Votre progression ne sera pas sauvegardée.')) {
                  setGameState('intro');
                  setCurrentIndex(0);
                  setAnswers([]);
                  setQuestions([]);
                }
              }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-950/80 hover:bg-red-900 transition-colors border border-red-500/30"
            >
              <ArrowLeft className="w-4 h-4 text-red-400" />
              <span className="text-sm text-white">Quitter</span>
            </button>
            <div className="text-white text-sm font-medium truncate max-w-[150px] sm:max-w-none">
              {game?.title}
            </div>
            {player && (
              <Link 
                to="/mon-espace-joueur"
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition-colors shadow-lg shadow-red-500/30"
              >
                <User className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-semibold hidden sm:inline">Mon Espace Joueur</span>
              </Link>
            )}
          </div>
        </header>

        <div className="p-4 pt-20 relative z-10">
        <div className="max-w-2xl mx-auto pt-8">
          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm text-white/60 mb-2">
              <span>Question {currentIndex + 1} / {questions.length}</span>
              <span>{currentQuestion.points} points</span>
            </div>
            <Progress value={progressPercent} className="h-2 [&>div]:bg-red-500" />
          </div>

          {/* Timer */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="flex items-center gap-1 text-white/60">
                <Clock className="w-4 h-4" />
                Temps restant
              </span>
              <span className={`font-bold ${timeLeft <= 10 ? 'text-yellow-400 animate-pulse' : 'text-white'}`}>
                {timeLeft}s
              </span>
            </div>
            <Progress
              value={timePercent}
              className={`h-3 ${timeLeft <= 10 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500'}`}
            />
          </div>

          {/* Question */}
          <Card className="bg-black border-2 border-red-500/50 mb-6 shadow-xl shadow-red-500/10">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-white mb-2">
                {currentQuestion.question_text}
              </h2>
              {currentQuestion.media_url && currentQuestion.question_type === 'image' && (
                <img
                  src={currentQuestion.media_url}
                  alt="Question"
                  className="w-full rounded-lg mt-4 max-h-64 object-contain"
                />
              )}
            </CardContent>
          </Card>

          {/* Options */}
          <div className="grid grid-cols-1 gap-3">
            {currentQuestion.options?.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className="w-full p-4 bg-red-950/80 border border-red-500/30 rounded-xl text-left text-white hover:bg-red-900/80 hover:border-red-400/50 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-900 flex items-center justify-center text-sm font-bold group-hover:bg-red-600 transition-colors border border-red-500/30">
                    {String.fromCharCode(65 + idx)}
                  </div>
                  <span>{option.text}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Submitting state
  if (gameState === 'submitting') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        <MatrixRain />
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-white">Calcul de votre score...</p>
        </div>
      </div>
    );
  }

  // Finished state
  if (gameState === 'finished') {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        <MatrixRain />
        <div className="fixed inset-0 bg-gradient-to-b from-red-900/20 via-black/30 to-red-900/20 pointer-events-none z-0" />
        
        {/* Header */}
        <header className="border-b border-red-500/30 bg-black/95 backdrop-blur-md fixed top-0 left-0 right-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/le-coffre-msc" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-950/80 hover:bg-red-900 transition-colors border border-red-500/30">
              <ArrowLeft className="w-4 h-4 text-red-400" />
              <span className="text-sm text-white">Retour</span>
            </Link>
            {player && (
              <Link 
                to="/mon-espace-joueur" 
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition-colors shadow-lg shadow-red-500/30"
              >
                <User className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-semibold">Mon Espace Joueur</span>
              </Link>
            )}
          </div>
        </header>

        <div className="flex items-center justify-center p-4 min-h-screen pt-20 relative z-10">
          <Card className="max-w-lg w-full bg-black border-2 border-red-500/50 shadow-xl shadow-red-500/20">
          <CardHeader className="text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/40">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">Quiz terminé !</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {results && (
              <>
                <div className="text-center">
                  <p className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-red-600 mb-2">
                    {results.score}
                  </p>
                  <p className="text-white/60">points</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-500/10 rounded-lg p-4 text-center border border-green-500/30">
                    <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-400">{results.correct_count}</p>
                    <p className="text-sm text-white/60">Bonnes réponses</p>
                  </div>
                  <div className="bg-red-500/10 rounded-lg p-4 text-center border border-red-500/30">
                    <XCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-400">{results.total_questions - results.correct_count}</p>
                    <p className="text-sm text-white/60">Mauvaises réponses</p>
                  </div>
                </div>

                {/* Message sur le caractère gagné */}
                {results.correct_count >= (results.total_questions / 2) && game?.month && (
                  <div className="bg-gradient-to-br from-green-500/20 via-emerald-500/10 to-green-600/20 border-2 border-green-500/50 rounded-lg p-6 text-center relative overflow-hidden animate-in fade-in zoom-in duration-500">
                    {/* Animated sparkles background */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      <div className="absolute top-2 left-4 text-yellow-400 animate-bounce delay-100">✨</div>
                      <div className="absolute top-4 right-6 text-yellow-400 animate-bounce delay-300">⭐</div>
                      <div className="absolute bottom-3 left-8 text-yellow-400 animate-bounce delay-500">✨</div>
                      <div className="absolute bottom-2 right-4 text-yellow-400 animate-bounce delay-200">🌟</div>
                    </div>
                    
                    <div className="relative z-10">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-green-500/50 animate-pulse">
                        <Sparkles className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-green-400 font-bold text-xl mb-2">🎉 CARACTÈRE DÉBLOQUÉ ! 🎉</p>
                      
                      {/* Show the earned character with position in combination */}
                      {results.earned_character && (
                        <div className="my-4">
                          <p className="text-white/60 text-sm mb-3">Votre nouveau caractère :</p>
                          
                          {/* Animated character reveal - RED for quiz */}
                          <div className="inline-block px-8 py-4 bg-red-600 rounded-xl border-2 border-red-400 shadow-lg shadow-red-500/30 animate-pulse">
                            <span className="text-5xl font-mono font-bold text-white drop-shadow-lg">{results.earned_character}</span>
                          </div>
                          
                          {/* Position in combination */}
                          <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
                            <p className="text-white/50 text-xs mb-2">Position dans la combinaison :</p>
                            <div className="flex justify-center gap-1 flex-wrap">
                              {Array.from({length: 24}).map((_, i) => {
                                const monthIndex = Math.floor(i / 2);
                                const isQuizChar = i % 2 === 0;
                                const isCurrentPosition = monthIndex + 1 === game.month && isQuizChar;
                                
                                return (
                                  <div 
                                    key={i}
                                    className={`w-5 h-6 flex items-center justify-center rounded text-xs font-mono ${
                                      isCurrentPosition 
                                        ? 'bg-red-600 text-white border-2 border-red-300 animate-bounce shadow-lg shadow-red-500/50' 
                                        : isQuizChar
                                          ? 'bg-red-500/20 text-red-300/50 border border-red-500/20'
                                          : 'bg-white/20 text-white/50 border border-white/20'
                                    }`}
                                  >
                                    {isCurrentPosition ? results.earned_character : '?'}
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-red-400 text-xs mt-2">
                              Position {(game.month - 1) * 2 + 1}/24 • Quiz de {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][game.month - 1]}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-white/80 text-sm">
                        Bravo ! Ce caractère a été ajouté à votre combinaison du coffre !
                      </p>
                      
                      <p className="text-amber-400 text-xs mt-3 animate-pulse">
                        ⏳ Redirection vers votre espace joueur dans quelques secondes...
                      </p>
                      
                      <Link to="/ma-progression-coffre" className="inline-block mt-4">
                        <Button className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold px-6">
                          <Gift className="w-4 h-4 mr-2" />
                          Voir ma combinaison maintenant
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}

                {/* Message si le joueur n'a pas validé le quiz */}
                {results.correct_count < (results.total_questions / 2) && game?.month && (
                  <div className="bg-gradient-to-br from-slate-900 via-red-950/30 to-slate-900 border-2 border-red-500/40 rounded-xl p-6 text-center">
                    {/* Sad emoji animation */}
                    <div className="text-5xl mb-3 animate-bounce">😔</div>
                    
                    <p className="text-red-400 font-bold text-xl mb-2">Dommage...</p>
                    <p className="text-white/80 text-sm mb-1">
                      Vous avez obtenu <strong className="text-red-300">{results.correct_count}/{results.total_questions}</strong> bonnes réponses.
                    </p>
                    <p className="text-white/60 text-sm mb-4">
                      Il faut minimum <strong className="text-amber-400">50%</strong> pour débloquer le caractère.
                    </p>
                    
                    {/* Encouragement message */}
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-5">
                      <p className="text-amber-400 text-sm font-medium">
                        💪 Ne vous découragez pas ! Vous pouvez retenter votre chance.
                      </p>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button 
                        onClick={() => {
                          setCelebrationTriggered(false);
                          setGameState('intro');
                          setAnswers([]);
                          setResults(null);
                          setCurrentIndex(0);
                          setTimeLeft(game?.time_limit_seconds || 60);
                        }}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold px-6 py-3 text-lg shadow-lg shadow-green-500/30"
                      >
                        <RefreshCw className="w-5 h-5 mr-2" />
                        Retenter maintenant
                      </Button>
                      
                      <Link to="/mon-espace-joueur">
                        <Button 
                          variant="outline"
                          className="border-white/30 text-white hover:bg-white/10 px-6 py-3"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Plus tard
                        </Button>
                      </Link>
                    </div>
                    
                    <p className="text-white/40 text-xs mt-4">
                      Astuce : Prenez le temps de bien lire chaque question !
                    </p>
                  </div>
                )}

                <div className="bg-red-950/80 rounded-lg p-4 text-center border border-red-500/30">
                  <Clock className="w-6 h-6 text-red-400 mx-auto mb-2" />
                  <p className="text-sm text-white/60">Temps total</p>
                  <p className="text-lg font-semibold text-white">
                    {Math.round(results.total_time_ms / 1000)}s
                  </p>
                </div>
              </>
            )}

            <div className="flex gap-3">
              <Link to="/mon-espace-joueur" className="flex-1">
                <Button
                  variant="outline"
                  className="w-full border-red-500/50 text-white hover:bg-red-950"
                >
                  <User className="w-4 h-4 mr-2" />
                  Mon profil
                </Button>
              </Link>
              <Link to="/le-coffre-msc" className="flex-1">
                <Button className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800">
                  Le Coffre MSC
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  return null;
};

export default GamePlayPage;
